import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  HiOutlineAcademicCap,
  HiOutlineCalendar,
  HiOutlineCheckCircle,
  HiOutlineClipboardList,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineLockClosed,
  HiOutlineRefresh,
  HiOutlineTrash,
  HiOutlineX,
} from 'react-icons/hi';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../context/AuthContext';
import BrandLoader from '../../components/feedback/BrandLoader';

function AdminGate() {
  const { user, loading: authLoading, signOut } = useAuth();
  const [adminUser, setAdminUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState('');

  const checkAdminAccess = useCallback(async () => {
    if (authLoading) return;

    if (!user) {
      setAdminUser(null);
      setChecking(false);
      return;
    }

    setChecking(true);
    setError('');

    try {
      const { data: adminData, error: adminError } = await supabase
        .from('admin_users')
        .select('user_id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();

      if (adminError) throw adminError;
      if (!adminData) {
        throw new Error('This account does not have admin access.');
      }

      setAdminUser(user);
    } catch (err) {
      console.error(err);
      setAdminUser(null);
      setError(err?.message || 'Could not verify admin access.');
    } finally {
      setChecking(false);
    }
  }, [authLoading, user]);

  useEffect(() => {
    checkAdminAccess();
  }, [checkAdminAccess]);

  if (authLoading || checking) {
    return <BrandLoader />;
  }

  if (!user) {
    return <AdminLogin />;
  }

  if (!adminUser) {
    return (
      <AdminAccessMessage
        title="No admin access"
        text={error || 'This signed-in account is not listed as an admin.'}
        onSignOut={signOut}
      />
    );
  }

  return <AdminPanel adminUser={adminUser} onLock={() => setAdminUser(null)} signOut={signOut} />;
}

function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (signInError) throw signInError;
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Could not sign in.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-pattern-bg min-h-screen px-4 py-8 text-white">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md items-center">
        <form onSubmit={handleSubmit} className="w-full rounded-2xl border border-gray-800 bg-neutral-800 p-6 shadow-sm">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500 text-white">
              <HiOutlineLockClosed className="text-2xl" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Admin</p>
              <h1 className="text-2xl font-bold">Secure Sign In</h1>
            </div>
          </div>

          <div className="space-y-4">
            <label className="block">
              <span className="mb-1 block text-sm font-semibold text-gray-300">Email</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full rounded-xl border border-gray-700 bg-gray-950 px-4 py-3 text-white outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-semibold text-gray-300">Password</span>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full rounded-xl border border-gray-700 bg-gray-950 px-4 py-3 pr-12 text-white outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(current => !current)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 transition hover:text-gray-200"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <HiOutlineEyeOff className="text-xl" /> : <HiOutlineEye className="text-xl" />}
                </button>
              </div>
            </label>
          </div>

          {error && (
            <div className="mt-4 rounded-xl border border-red-900 bg-red-950 px-4 py-3 text-sm font-medium text-red-300">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="mt-5 w-full rounded-xl bg-blue-500 px-5 py-3 font-semibold text-white transition hover:bg-blue-600 disabled:bg-neutral-800 disabled:text-neutral-500"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

function AdminAccessMessage({ title, text, onSignOut }) {
  return (
    <div className="min-h-screen bg-gray-950 px-4 py-8 text-white">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md items-center">
        <div className="w-full rounded-2xl border border-gray-800 bg-neutral-800 p-6 shadow-sm">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500 text-white">
              <HiOutlineLockClosed className="text-2xl" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Admin</p>
              <h1 className="text-2xl font-bold">{title}</h1>
            </div>
          </div>

          <p className="text-sm text-gray-400">{text}</p>

          <div className="mt-5 flex flex-col gap-3">
            {onSignOut && (
              <button
                type="button"
                onClick={onSignOut}
                className="w-full rounded-xl border border-gray-700 px-5 py-3 font-semibold text-gray-300 transition hover:bg-neutral-700"
              >
                Sign Out
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminPanel({ adminUser, onLock, signOut }) {
  const [schools, setSchools] = useState([]);
  const [sections, setSections] = useState([]);
  const [records, setRecords] = useState([]);
  const [terms, setTerms] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState('');
  const [termTransferSchool, setTermTransferSchool] = useState(null);
  const [schoolToDelete, setSchoolToDelete] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  const fetchAdminData = useCallback(async () => {
    setLoading(true);
    setError('');
    setStatus('');

    try {
      const [
        { data: schoolsData, error: schoolsError },
        { data: sectionsData, error: sectionsError },
        { data: recordsData, error: recordsError },
        { data: termsData, error: termsError },
      ] = await Promise.all([
        supabase.from('schools').select('id, name, created_at, owner_id, current_term_id').order('created_at', { ascending: false }),
        supabase.from('sections').select('id, school_id, name, initials, fields, created_at'),
        supabase.from('records').select('id, school_id, section_id, term_id, created_at'),
        supabase.from('terms').select('*').order('created_at', { ascending: false }),
      ]);

      if (schoolsError) throw schoolsError;
      if (sectionsError) throw sectionsError;
      if (recordsError) throw recordsError;
      if (termsError) throw termsError;

      setSchools(schoolsData || []);
      setSections(sectionsData || []);
      setRecords(recordsData || []);
      setTerms(termsData || []);
      setSelectedSchoolId(current => current || schoolsData?.[0]?.id || '');
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Could not load admin data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  const rows = useMemo(() => schools.map(school => ({
    ...school,
    sections: sections.filter(section => section.school_id === school.id),
    records: records.filter(record => record.school_id === school.id),
    terms: terms.filter(term => term.school_id === school.id),
  })).map(school => {
    const currentTerm = getCurrentTerm(school, school.terms);
    const missingTermCount = school.records.filter(record => !record.term_id).length;

    return {
      ...school,
      currentTerm,
      sectionCount: school.sections.length,
      recordCount: school.records.length,
      termCount: school.terms.length,
      missingTermCount,
      health: getSchoolHealth(school, currentTerm, missingTermCount),
    };
  }), [schools, sections, records, terms]);

  const selectedSchool = rows.find(school => school.id === selectedSchoolId) || rows[0];
  const totalArchivedTerms = terms.filter(term => term.status === 'archived').length;
  const schoolsMissingTerms = rows.filter(row => !row.currentTerm).length;
  const recordsMissingTerms = records.filter(record => !record.term_id).length;

  const openTermTransfer = (school) => {
    setTermTransferSchool(school);
    setConfirmText('');
    setError('');
    setStatus('');
  };

  const closeTermTransfer = () => {
    setTermTransferSchool(null);
    setConfirmText('');
  };

  const openDeleteSchool = (school) => {
    setSchoolToDelete(school);
    setDeleteConfirmText('');
    setError('');
    setStatus('');
  };

  const closeDeleteSchool = () => {
    setSchoolToDelete(null);
    setDeleteConfirmText('');
  };

  const startNextTermForSchool = async () => {
    if (!termTransferSchool?.currentTerm) return;

    const nextTerm = getNextTermDetails(termTransferSchool.currentTerm);
    setActionLoading(true);
    setError('');
    setStatus('');

    try {
      const today = new Date().toISOString().slice(0, 10);

      const { error: archiveError } = await supabase
        .from('terms')
        .update({ status: 'archived', ends_at: today })
        .eq('id', termTransferSchool.currentTerm.id);

      if (archiveError) throw archiveError;

      const { data: newTerm, error: newTermError } = await supabase
        .from('terms')
        .insert({
          school_id: termTransferSchool.id,
          name: nextTerm.name,
          academic_year: nextTerm.academicYear,
          status: 'active',
          starts_at: today,
        })
        .select()
        .single();

      if (newTermError) throw newTermError;

      const { error: schoolError } = await supabase
        .from('schools')
        .update({ current_term_id: newTerm.id })
        .eq('id', termTransferSchool.id);

      if (schoolError) throw schoolError;

      closeTermTransfer();
      setStatus(`${termTransferSchool.name} is now on ${formatTermLabel(newTerm)}.`);
      await fetchAdminData();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Could not start the next term for this school.');
    } finally {
      setActionLoading(false);
    }
  };

  const lockAdmin = async () => {
    await signOut();
    onLock();
  };

  const deleteSchool = async () => {
    if (!schoolToDelete?.id) return;

    setActionLoading(true);
    setError('');
    setStatus('');

    try {
      const { error: clearCurrentTermError } = await supabase
        .from('schools')
        .update({ current_term_id: null })
        .eq('id', schoolToDelete.id);

      if (clearCurrentTermError) throw clearCurrentTermError;

      const { error: recordsError } = await supabase
        .from('records')
        .delete()
        .eq('school_id', schoolToDelete.id);

      if (recordsError) throw recordsError;

      const { error: sectionsError } = await supabase
        .from('sections')
        .delete()
        .eq('school_id', schoolToDelete.id);

      if (sectionsError) throw sectionsError;

      const { error: termsError } = await supabase
        .from('terms')
        .delete()
        .eq('school_id', schoolToDelete.id);

      if (termsError) throw termsError;

      const { error: schoolError } = await supabase
        .from('schools')
        .delete()
        .eq('id', schoolToDelete.id);

      if (schoolError) throw schoolError;

      const deletedName = schoolToDelete.name;
      closeDeleteSchool();
      setSelectedSchoolId('');
      setStatus(`${deletedName} was deleted.`);
      await fetchAdminData();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Could not delete this school.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6 text-gray-900 dark:bg-neutral-950 dark:text-neutral-100 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-neutral-500">Admin Panel</p>
            <h1 className="mt-1 text-2xl font-bold">Platform Overview</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-neutral-400">{adminUser.email}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchAdminData}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800"
            >
              <HiOutlineRefresh />
              Refresh
            </button>
            <button
              onClick={lockAdmin}
              className="rounded-xl bg-neutral-800 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-neutral-700 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-white"
            >
              Lock
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-600 dark:border-red-800 dark:bg-neutral-900 dark:text-red-300">
            {error}
          </div>
        )}

        {status && (
          <div className="rounded-xl border border-green-100 bg-green-50 px-4 py-3 text-sm font-medium text-green-700 dark:border-green-800 dark:bg-neutral-900 dark:text-green-300">
            {status}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <AdminStat label="Schools" value={schools.length} icon={<HiOutlineAcademicCap />} />
          <AdminStat label="Records" value={records.length} icon={<HiOutlineClipboardList />} />
          <AdminStat label="Archived Terms" value={totalArchivedTerms} icon={<HiOutlineCalendar />} />
          <AdminStat label="Needs Setup" value={schoolsMissingTerms + recordsMissingTerms} icon={<HiOutlineCheckCircle />} />
        </div>

        <div className="grid grid-cols-1 gap-5 2xl:grid-cols-12">
          <section className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900 2xl:col-span-7">
            <div className="border-b border-gray-100 px-5 py-4 dark:border-neutral-800">
              <h2 className="text-lg font-bold">Schools</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-neutral-400">Select a school to inspect its terms, sections, and record health.</p>
            </div>

            {loading ? (
              <PanelLoader message="Loading admin data..." />
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-neutral-800">
                {rows.map(row => (
                  <button
                    type="button"
                    key={row.id}
                    onClick={() => setSelectedSchoolId(row.id)}
                    className={`grid w-full grid-cols-1 gap-3 px-5 py-4 text-left transition hover:bg-gray-50 dark:hover:bg-neutral-800/80 md:grid-cols-[1fr_auto] ${
                      selectedSchool?.id === row.id ? 'bg-blue-50 dark:bg-neutral-800' : ''
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate font-bold text-gray-900 dark:text-neutral-100">{row.name}</h3>
                        <HealthPill health={row.health} />
                      </div>
                      <p className="mt-1 text-sm text-gray-500 dark:text-neutral-400">
                        {row.currentTerm ? formatTermLabel(row.currentTerm) : 'No current term'} - {formatDate(row.created_at)}
                      </p>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <MiniMetric label="Sections" value={row.sectionCount} />
                      <MiniMetric label="Terms" value={row.termCount} />
                      <MiniMetric label="Records" value={row.recordCount} />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>

          <SchoolDetail
            school={selectedSchool}
            onStartNextTerm={openTermTransfer}
            onDeleteSchool={openDeleteSchool}
          />
        </div>
      </div>

      {termTransferSchool && (
        <TermTransferModal
          school={termTransferSchool}
          confirmText={confirmText}
          setConfirmText={setConfirmText}
          loading={actionLoading}
          onClose={closeTermTransfer}
          onConfirm={startNextTermForSchool}
        />
      )}

      {schoolToDelete && (
        <DeleteSchoolModal
          school={schoolToDelete}
          confirmText={deleteConfirmText}
          setConfirmText={setDeleteConfirmText}
          loading={actionLoading}
          onClose={closeDeleteSchool}
          onConfirm={deleteSchool}
        />
      )}
    </div>
  );
}

function AdminStat({ label, value, icon }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-neutral-500">{label}</p>
        <span className="text-xl text-blue-600 dark:text-neutral-200">{icon}</span>
      </div>
      <p className="mt-2 text-3xl font-bold">{value}</p>
    </div>
  );
}

function MiniMetric({ label, value }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-white px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900">
      <p className="text-xs font-semibold text-gray-400 dark:text-neutral-500">{label}</p>
      <p className="font-bold text-gray-800 dark:text-neutral-100">{value}</p>
    </div>
  );
}

function PanelLoader({ message }) {
  return (
    <div className="px-5 py-12 text-center">
      <div className="mx-auto mb-3 h-9 w-9 rounded-full border-2 border-blue-100 border-t-blue-600 animate-spin dark:border-neutral-800 dark:border-t-blue-400" />
      <p className="text-sm font-medium text-gray-400 dark:text-neutral-500">{message}</p>
    </div>
  );
}

function SchoolDetail({ school, onStartNextTerm, onDeleteSchool }) {
  if (!school) {
    return (
      <section className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm dark:border-neutral-800 dark:bg-neutral-900 2xl:col-span-5">
        <p className="text-sm font-medium text-gray-400">Select a school to view support tools.</p>
      </section>
    );
  }

  const nextTerm = school.currentTerm ? getNextTermDetails(school.currentTerm) : null;
  const termRecordCounts = school.terms.map(term => ({
    ...term,
    recordCount: school.records.filter(record => record.term_id === term.id).length,
  }));

  return (
    <section className="rounded-xl border border-gray-100 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900 2xl:col-span-5">
      <div className="border-b border-gray-100 px-5 py-4 dark:border-neutral-800">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-neutral-500">School Detail</p>
            <h2 className="mt-1 text-xl font-bold">{school.name}</h2>
          </div>
          <HealthPill health={school.health} />
        </div>
      </div>

      <div className="space-y-5 p-5">
        <div className="grid grid-cols-2 gap-3">
          <DetailMetric label="Current Term" value={school.currentTerm ? formatTermLabel(school.currentTerm) : 'Missing'} />
          <DetailMetric label="Created" value={formatDate(school.created_at)} />
          <DetailMetric label="Sections" value={school.sectionCount} />
          <DetailMetric label="Records" value={school.recordCount} />
        </div>

        <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 dark:border-neutral-800 dark:bg-neutral-950">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-neutral-500">Term Support</p>
              <h3 className="mt-1 font-bold">
                {nextTerm ? `Next: ${nextTerm.name} ${nextTerm.academicYear}` : 'No active term found'}
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-neutral-400">
                Admin can help a school move forward when they are ready. Existing records stay in the archived term.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onStartNextTerm(school)}
            disabled={!school.currentTerm}
            className="mt-4 w-full rounded-xl bg-blue-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400"
          >
            Start Next Term for School
          </button>
        </div>

        <div>
          <p className="mb-3 text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-neutral-500">Term History</p>
          <div className="space-y-2">
            {termRecordCounts.length === 0 ? (
              <p className="rounded-xl border border-dashed border-gray-200 px-4 py-5 text-center text-sm text-gray-400 dark:border-neutral-800">
                No terms found for this school.
              </p>
            ) : (
              termRecordCounts.map(term => (
                <div key={term.id} className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 px-4 py-3 dark:border-neutral-800">
                  <div>
                    <p className="font-semibold">{formatTermLabel(term)}</p>
                    <p className="text-xs text-gray-400 dark:text-neutral-500">{term.status === 'active' ? 'Current active term' : 'Archived term'}</p>
                  </div>
                  <span className="rounded-lg bg-gray-100 px-3 py-1 text-sm font-bold text-gray-600 dark:bg-neutral-800 dark:text-neutral-300">
                    {term.recordCount}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        <div>
          <p className="mb-3 text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-neutral-500">Setup Health</p>
          <div className="space-y-2 text-sm">
            <HealthLine ok={Boolean(school.currentTerm)} text={school.currentTerm ? 'Current term is set.' : 'Current term is missing.'} />
            <HealthLine ok={school.missingTermCount === 0} text={school.missingTermCount === 0 ? 'All records have term IDs.' : `${school.missingTermCount} records are missing term IDs.`} />
            <HealthLine ok={school.sectionCount > 0} text={school.sectionCount > 0 ? 'Sections are configured.' : 'No sections have been created.'} />
          </div>
        </div>

        <div className="rounded-xl border border-red-100 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/30">
          <p className="text-xs font-bold uppercase tracking-wider text-red-600 dark:text-red-300">Danger Zone</p>
          <p className="mt-1 text-sm text-red-700 dark:text-red-200">
            Delete this school and all its sections, terms, and records.
          </p>
          <button
            type="button"
            onClick={() => onDeleteSchool(school)}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700"
          >
            <HiOutlineTrash />
            Delete School
          </button>
        </div>
      </div>
    </section>
  );
}

function DetailMetric({ label, value }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-950">
      <p className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-neutral-500">{label}</p>
      <p className="mt-1 truncate font-bold text-gray-900 dark:text-neutral-100">{value}</p>
    </div>
  );
}

function HealthLine({ ok, text }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-gray-100 px-3 py-2 dark:border-neutral-800">
      <span className={ok ? 'text-green-600 dark:text-green-300' : 'text-blue-500 dark:text-blue-300'}>
        <HiOutlineCheckCircle />
      </span>
      <span className="text-gray-600 dark:text-neutral-300">{text}</span>
    </div>
  );
}

function HealthPill({ health }) {
  const styles = {
    good: 'border-green-100 bg-green-50 text-green-700 dark:border-green-800 dark:bg-neutral-900 dark:text-green-300',
    warning: 'border-blue-100 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-neutral-900 dark:text-blue-300',
  };

  return (
    <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${styles[health.type] || styles.warning}`}>
      {health.label}
    </span>
  );
}

function TermTransferModal({ school, confirmText, setConfirmText, loading, onClose, onConfirm }) {
  const nextTerm = getNextTermDetails(school.currentTerm);
  const canConfirm = confirmText.trim().toUpperCase() === 'START TERM';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/70 px-4 py-6">
      <div className="w-full max-w-lg rounded-xl border border-gray-100 bg-white shadow-xl dark:border-neutral-800 dark:bg-neutral-900">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-neutral-800">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-neutral-500">Admin Term Transfer</p>
            <h3 className="mt-1 text-lg font-bold">Start {nextTerm.name} {nextTerm.academicYear}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 transition hover:text-gray-700 dark:hover:text-neutral-200"
            aria-label="Close term transfer"
          >
            <HiOutlineX className="text-xl" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <p className="text-sm text-gray-600 dark:text-neutral-300">
            This will archive {formatTermLabel(school.currentTerm)} for {school.name}. Existing records remain accessible, and new records will save under {nextTerm.name} {nextTerm.academicYear}.
          </p>

          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-gray-700 dark:text-neutral-200">
              Type START TERM to confirm
            </span>
            <input
              value={confirmText}
              onChange={(event) => setConfirmText(event.target.value)}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 outline-none focus:ring-2 focus:ring-blue-100 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100"
            />
          </label>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-gray-100 px-5 py-4 dark:border-neutral-800 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 disabled:text-gray-300 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading || !canConfirm}
            className="rounded-xl bg-blue-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400"
          >
            {loading ? 'Starting...' : 'Start Next Term'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteSchoolModal({ school, confirmText, setConfirmText, loading, onClose, onConfirm }) {
  const canConfirm = confirmText.trim().toUpperCase() === 'DELETE';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/70 px-4 py-6">
      <div className="w-full max-w-lg rounded-xl border border-gray-100 bg-white shadow-xl dark:border-neutral-800 dark:bg-neutral-900">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-neutral-800">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-red-500">Delete School</p>
            <h3 className="mt-1 text-lg font-bold">Delete {school.name}?</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 transition hover:text-gray-700 disabled:opacity-50 dark:hover:text-neutral-200"
            aria-label="Close delete school"
          >
            <HiOutlineX className="text-xl" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <p className="text-sm text-gray-600 dark:text-neutral-300">
            This permanently deletes the school profile, its sections, terms, and records. This does not delete the Supabase Auth user account.
          </p>

          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-gray-700 dark:text-neutral-200">
              Type DELETE to confirm
            </span>
            <input
              value={confirmText}
              onChange={(event) => setConfirmText(event.target.value)}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 outline-none focus:ring-2 focus:ring-red-100 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100"
            />
          </label>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-gray-100 px-5 py-4 dark:border-neutral-800 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 disabled:text-gray-300 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading || !canConfirm}
            className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:bg-gray-200 disabled:text-gray-400"
          >
            {loading ? 'Deleting...' : 'Delete School'}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(value) {
  if (!value) return '-';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatTermLabel(term) {
  return `${term.name} ${term.academic_year}`;
}

function getCurrentTerm(school, termList) {
  return termList.find(term => term.id === school.current_term_id)
    || termList.find(term => term.status === 'active')
    || null;
}

function getSchoolHealth(school, currentTerm, missingTermCount) {
  if (!currentTerm || missingTermCount > 0 || school.sections.length === 0) {
    return { type: 'warning', label: 'Needs Check' };
  }

  return { type: 'good', label: 'Healthy' };
}

function getNextTermDetails(term) {
  if (term.name === 'First Term') {
    return { name: 'Second Term', academicYear: term.academic_year };
  }

  if (term.name === 'Second Term') {
    return { name: 'Third Term', academicYear: term.academic_year };
  }

  const [start, end] = String(term.academic_year || '').split('/').map(value => Number(value));

  return {
    name: 'First Term',
    academicYear: start && end ? `${start + 1}/${end + 1}` : '2026/2027',
  };
}

export default AdminGate;
